import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, MailCheck } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  apexDomain,
  currentClubSlug,
  goToClubHost,
  hostKind,
  stashSocialOriginClub,
  takeSocialOriginClub,
} from "@/lib/hostKind";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import type { KratosFlow, KratosSuccessPayload } from "@/lib/kratos";
import {
  firstFlowError,
  getFlow,
  kratosErrorCopy,
  nodeMessages,
  oidcProviders,
  submitOidcProvider,
} from "@/lib/kratos";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";
import { KratosVerificationForm } from "@/features/auth/kratos/KratosVerificationForm";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";

/**
 * Kratos-mode account creation: register (name/email/password) → enter
 * the emailed verification code → exchange into a Tktic app session.
 * Registration issues a Kratos session immediately (session hook), so
 * no second login is needed; the exchange only passes once the email
 * is verified.
 */
export function KratosRegisterPanel({
  nextPath,
  autoExchange = false,
  socialStartProvider = null,
  socialLoginEnabled = false,
  googleEnabled = true,
  appleEnabled = true,
}: {
  nextPath?: string;
  autoExchange?: boolean;
  /** On the APEX, auto-start this OIDC provider's registration flow (a
   * club-host social click redirects here so the ceremony is same-origin
   * with the callback). See handleSocialClick + docs/KRATOS.md. */
  socialStartProvider?: string | null;
  socialLoginEnabled?: boolean;
  googleEnabled?: boolean;
  appleEnabled?: boolean;
} = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationFlow, setVerificationFlow] = useState<KratosFlow | null>(null);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [socialBusy, setSocialBusy] = useState<string | null>(null);
  const socialReturnTo = useMemo(
    () => socialAuthReturnTo("/register", nextPath),
    [nextPath],
  );

  const handleRegistered = async ({ data }: { data: KratosSuccessPayload }) => {
    const verification = (data.continue_with ?? []).find(
      (item) => item.action === "show_verification_ui" && item.flow?.id,
    );
    if (verification?.flow?.id) {
      try {
        setVerificationFlow(await getFlow("verification", verification.flow.id));
        return;
      } catch {
        // Fall through — the verification form creates a fresh flow.
      }
    }
    // No verification step handed back (e.g. already-verified address):
    // try the exchange directly.
    await finishExchange();
  };

  const finishExchange = async () => {
    setExchangeError(null);
    try {
      await exchangeKratosSession();
      // Parent RegisterPage keys off `useMe()` — once the app session
      // exists and the profile email is verified it advances to the
      // team-access request step.
      await queryClient.invalidateQueries();
      // AWAIT the `me` refetch (not just invalidate) before navigating: a
      // deep-linked flow like the club-admin invite mounts its target signed-in
      // and immediately auto-accepts, so `me` MUST be populated first — else the
      // invite page briefly renders the unauthenticated branch and the accept
      // can miss (the club-admin invite create-account bug).
      await queryClient.refetchQueries({ queryKey: unscopedKey("me") });
      // Multi-club social-signup safety net: the OIDC round-trip returns to the
      // apex callback, so a user who started registering on `<club>.<apex>` can
      // land here on the bare apex. Send them back to the club they started on
      // (stashed cookie) — new social signups usually have no membership yet,
      // so the cookie is the reliable signal. See finishSignIn in
      // KratosLoginPanel + docs/KRATOS.md.
      if (hostKind() === "apex") {
        const origin = takeSocialOriginClub();
        if (origin) {
          goToClubHost(origin, nextPath !== "/hub" ? nextPath : "/hub");
          return;
        }
      }
      // Deep-linked flows (e.g. team invites) return to their origin;
      // organic registrations stay here for the team-access step.
      if (nextPath && nextPath !== "/hub") {
        void navigate({ to: nextPath, replace: true });
      }
    } catch (err) {
      setExchangeError(exchangeErrorCopy(err));
    }
  };

  const { flow, loading, submitting, error, submit } = useKratosFlow("registration", {
    createParams: { return_to: socialReturnTo },
    onSuccess: handleRegistered,
  });

  const autoExchangeStarted = useRef(false);
  useEffect(() => {
    if (!autoExchange || autoExchangeStarted.current) return;
    autoExchangeStarted.current = true;
    void finishExchange();
    // One-shot resume after a provider redirects back to /register.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExchange]);

  // Apex auto-start: a club-host social click handed off to
  // `<apex>/register?provider=…&social-start=1`; once the registration flow is
  // ready here, submit that provider so the ceremony is same-origin with the
  // callback. One-shot per mount. MUST stay above the early returns below, or
  // the hook count changes between the loading and loaded renders.
  const socialStartRef = useRef(false);
  useEffect(() => {
    if (!socialStartProvider || socialStartRef.current) return;
    if (!flow || socialBusy) return;
    socialStartRef.current = true;
    void handleSocialClick(socialStartProvider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialStartProvider, flow]);

  if (verificationFlow) {
    return (
      <section className="flex flex-col gap-3" data-testid="register-kratos-verify">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded bg-accent text-accent-ink">
            <MailCheck aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="font-display text-lg font-semibold text-ink">
            Verify your email
          </h2>
        </div>
        <p className="text-sm text-ink-muted">
          We sent a one-time code to{" "}
          <span className="font-mono text-ink">{email || "your email"}</span>.
          Team access requests open after verification.
        </p>
        <KratosVerificationForm
          initialFlow={verificationFlow}
          email={email}
          onVerified={finishExchange}
          testIdPrefix="register-verify"
        />
        {exchangeError ? (
          <p
            data-testid="register-kratos-exchange-error"
            className="font-mono text-xs text-destructive"
          >
            {exchangeError}
          </p>
        ) : null}
      </section>
    );
  }

  if (loading) {
    return (
      <p data-testid="register-kratos-loading" className="font-mono text-xs text-ink-muted">
        Preparing account creation...
      </p>
    );
  }

  const flowError = flow ? firstFlowError(flow) : null;
  const emailMessages = flow ? nodeMessages(flow, "traits.email") : [];
  const passwordMessages = flow ? nodeMessages(flow, "password") : [];
  const socialProviders = flow
    ? oidcProviders(flow).filter((provider) =>
        socialProviderAllowed(provider.id, {
          socialLoginEnabled,
          googleEnabled,
          appleEnabled,
        }) && !provider.disabled,
      )
    : [];

  const handleSocialClick = async (providerId: string) => {
    if (socialBusy) return;
    // Same as the login panel: the OIDC ceremony must run on the apex so
    // Kratos's host-scoped `ory_kratos_continuity` cookie is readable at the
    // fixed apex callback. On a CLUB host, stash the origin club and hand off
    // to `<apex>/register?provider=…&social-start=1`; the apex register panel
    // auto-starts the flow same-origin. See KratosLoginPanel + docs/KRATOS.md.
    if (hostKind() !== "apex") {
      stashSocialOriginClub(currentClubSlug());
      const apexReg = new URL("/register", `https://${apexDomain()}`);
      apexReg.searchParams.set("provider", providerId);
      apexReg.searchParams.set("social-start", "1");
      if (nextPath && nextPath !== "/hub") {
        apexReg.searchParams.set("next", nextPath);
      }
      setSocialBusy(providerId);
      window.location.assign(apexReg.toString());
      return;
    }
    if (!flow) return;
    setSocialBusy(providerId);
    setSocialError(null);
    setExchangeError(null);
    try {
      const outcome = await submitOidcProvider("registration", flow, providerId);
      if (outcome.kind === "success") {
        await handleRegistered(outcome);
      } else if (outcome.kind === "flow") {
        setSocialError(firstFlowError(outcome.flow) ?? "Social registration could not start.");
        setSocialBusy(null);
      }
    } catch (err) {
      setSocialError(kratosErrorCopy(err));
      setSocialBusy(null);
    }
  };

  return (
    <section className="flex flex-col gap-4" data-testid="register-kratos-panel">
      {socialProviders.length ? (
        <div data-testid="register-social-providers" className="grid gap-2">
          {socialProviders.map((provider) => (
            <Button
              key={provider.id}
              type="button"
              variant="outline"
              onClick={() => void handleSocialClick(provider.id)}
              disabled={!!socialBusy || submitting}
              data-testid={`register-social-${provider.id.toLowerCase()}`}
              className="w-full min-w-0"
            >
              <SocialProviderMark providerId={provider.id} />
              <span className="truncate">
                {socialBusy === provider.id
                  ? "Opening..."
                  : `Continue with ${provider.label}`}
              </span>
            </Button>
          ))}
        </div>
      ) : null}
      <form
        data-testid="register-kratos-form"
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (submitting || !flow) return;
          void submit({
            method: "password",
            "traits.email": email.trim(),
            "traits.name": name.trim() || undefined,
            password,
          });
        }}
      >
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            data-testid="register-kratos-name"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            data-testid="register-kratos-email"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {emailMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            data-testid="register-kratos-password"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {passwordMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        {flowError ? (
          <p
            data-testid="register-kratos-error"
            className="font-mono text-xs text-destructive sm:col-span-2"
          >
            {flowError}
          </p>
        ) : null}
        {error ? (
          <p className="font-mono text-xs text-destructive sm:col-span-2">{error}</p>
        ) : null}
        {socialError ? (
          <p
            data-testid="register-social-error"
            className="font-mono text-xs text-destructive sm:col-span-2"
          >
            {socialError}
          </p>
        ) : null}
        {exchangeError ? (
          <p
            data-testid="register-kratos-exchange-error"
            className="font-mono text-xs text-destructive sm:col-span-2"
          >
            {exchangeError}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-3 sm:col-span-2">
          <Link to="/login" className="text-xs text-ink-muted hover:text-ink">
            Already have an account?
          </Link>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="register-kratos-submit"
          >
            {submitting ? (
              "Creating…"
            ) : (
              <>
                Create account
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}

function socialAuthReturnTo(pathname: "/login" | "/register", nextPath?: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set("social", "1");
  if (nextPath && nextPath !== "/hub") {
    url.searchParams.set("next", nextPath);
  }
  return url.toString();
}

function socialProviderAllowed(
  providerId: string,
  options: {
    socialLoginEnabled: boolean;
    googleEnabled: boolean;
    appleEnabled: boolean;
  },
): boolean {
  if (!options.socialLoginEnabled) return false;
  const id = providerId.trim().toLowerCase();
  if (id === "google") return options.googleEnabled;
  if (id === "apple") return options.appleEnabled;
  return false;
}

function SocialProviderMark({ providerId }: { providerId: string }) {
  const id = providerId.trim().toLowerCase();
  const label = id === "google" ? "G" : id === "apple" ? "ID" : "SSO";
  return (
    <span
      aria-hidden="true"
      className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-rule bg-elevated px-1 font-display text-[0.62rem] font-semibold uppercase leading-none text-ink"
    >
      {label}
    </span>
  );
}

function exchangeErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "verified_email_required") {
      return "Your email still shows as unverified. Enter the code from your inbox first.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social registration option is currently disabled.";
    }
    if (code === "registration_disabled") {
      return "Account registration is currently closed. Contact your club admin for access.";
    }
    if (code === "account_link_required") {
      return "This email already has a Tktic account. Sign in to link your social account.";
    }
    return "Account setup could not finish. Please try again.";
  }
  return "Network error. Try again.";
}
